import NotificationToScreen from "enums/NotificationToScreen";

export type NotificationTargetKind =
  | "transaction"
  | "card"
  | "invoice"
  | "contact"
  | "payment";

export interface NotificationTarget {
  kind: NotificationTargetKind;
  id: string;
}

/** Which shell the panel is in; each one opens only the drawers it has. */
export type NotificationSurface = "admin" | "employee" | "platform";

interface TargetSource {
  panelScreen?: string | null;
  dataId?: string | null;
  cardId?: string | null;
}

// Employees have no invoice, contact or payment drawers, and platform has no spend drawers.
const SURFACE_KINDS: Record<
  NotificationSurface,
  ReadonlySet<NotificationTargetKind>
> = {
  admin: new Set(["transaction", "card", "invoice", "contact", "payment"]),
  employee: new Set(["transaction", "card"]),
  platform: new Set(),
};

/** The shell a route belongs to, falling back to the least privileged one. */
export const surfaceForPath = (pathname: string): NotificationSurface => {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/platform")) return "platform";
  return "employee";
};

const withId = (
  kind: NotificationTargetKind,
  id?: string | null,
): NotificationTarget | null => (id ? { kind, id } : null);

const targetFor = (notification: TargetSource): NotificationTarget | null => {
  switch (notification.panelScreen) {
    case NotificationToScreen.TransactionDetails:
      return withId("transaction", notification.dataId);
    // A card request's dataId is the request, so both card screens open by cardId.
    case NotificationToScreen.RequestStatus:
    case NotificationToScreen.CardDetail:
      return withId("card", notification.cardId);
    case NotificationToScreen.InvoiceDetails:
      return withId("invoice", notification.dataId);
    case NotificationToScreen.ContactDetails:
      return withId("contact", notification.dataId);
    case NotificationToScreen.PaymentDetails:
      return withId("payment", notification.dataId);
    default:
      return null;
  }
};

/** What a click on this notification opens in this shell, or null when it opens nothing. */
export const notificationTarget = (
  notification: TargetSource,
  surface: NotificationSurface,
): NotificationTarget | null => {
  const target = targetFor(notification);
  return target && SURFACE_KINDS[surface].has(target.kind) ? target : null;
};
